import { Component, Input, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild, ChangeDetectorRef, NgZone } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { ConfigService } from 'src/app/services/config.service';
import { HttpClient } from '@angular/common/http';
import { KeycloakService } from 'keycloak-angular';
import { WidgetContextService } from 'src/app/services/widget-context.service';

@Component({
    selector: 'app-browser-widget',
    templateUrl: './browser-widget.component.html',
    styleUrls: ['../base-widget/base-widget.component.scss', './browser-widget.component.scss'],
    standalone: false
})
export class BrowserWidgetComponent extends BaseWidgetComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('viewport') viewportEl!: ElementRef<HTMLImageElement>;
  @ViewChild('viewportContainer') viewportContainerEl!: ElementRef<HTMLDivElement>;
  @ViewChild('urlInput') urlInput!: ElementRef<HTMLInputElement>;

  @Input() sessionId: string = 'default';
  @Input() initialUrl: string = '';

  public currentUrl: string = '';
  public pageTitle: string = 'Browser';
  public urlBarValue: string = '';
  public connected: boolean = false;
  public loading: boolean = true;
  public error: string | null = null;
  public setupRequired: { message: string; instructions: string } | null = null;
  public frameCount: number = 0;
  public viewportWidth: number = 1280;
  public viewportHeight: number = 720;
  public tabs: { index: number; url: string; title: string; active: boolean }[] = [];

  private _abortController: AbortController | null = null;
  private _currentFrameUrl: string | null = null;
  private _reconnectTimer: any = null;
  private _destroyed = false;
  private _resizeObserver: ResizeObserver | null = null;
  private _resizeDebounceTimer: any = null;
  private _mousemoveThrottleTimer: any = null;
  private _mouseIsDown = false;
  private _dragStartPos: { x: number; y: number } | null = null;
  private _hasDragged = false;
  private _tabOrder: number[] = [];

  constructor(
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private keycloak: KeycloakService,
    private widgetContext: WidgetContextService,
  ) {
    super();
  }

  private async getAuthToken(): Promise<string> {
    if (ConfigService.localMode) return '';
    try {
      await this.keycloak.updateToken(30);
      return await this.keycloak.getToken() || '';
    } catch {
      return '';
    }
  }

  ngOnInit(): void {
    this.checkBrowserStatus();
  }

  private checkBrowserStatus(): void {
    const url = `${ConfigService.muxUrl}/browser/status`;
    this.http.get<any>(url).subscribe({
      next: (status) => {
        if (status.ready) {
          this.createSession();
        } else {
          this.setupRequired = {
            message: status.message,
            instructions: status.instructions,
          };
          this.loading = false;
          this.cdr.detectChanges();
        }
      },
      error: () => {
        this.createSession();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.viewportContainerEl?.nativeElement) {
      this._resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const w = Math.round(entry.contentRect.width);
        const h = Math.round(entry.contentRect.height);
        if (w > 0 && h > 0 && (w !== this.viewportWidth || h !== this.viewportHeight)) {
          clearTimeout(this._resizeDebounceTimer);
          this._resizeDebounceTimer = setTimeout(() => this.resizeViewport(w, h), 150);
        }
      });
      this._resizeObserver.observe(this.viewportContainerEl.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this._destroyed = true;
    this._resizeObserver?.disconnect();
    clearTimeout(this._resizeDebounceTimer);
    clearTimeout(this._mousemoveThrottleTimer);
    this.widgetContext.removeContext(this.id);
    super.ngOnDestroy();
    this.disconnect();
  }

  private updateWidgetContext(): void {
    this.widgetContext.updateContext({
      widgetId: this.id,
      widgetType: 'BrowserWidget',
      state: {
        url: this.currentUrl,
        title: this.pageTitle,
        sessionId: this.sessionId,
        connected: this.connected,
        tools: 'You can control this browser. Use your Playwright MCP tools (browser_navigate, browser_click, browser_type, browser_snapshot) if available. The user sees your actions in real time.',
      },
    });
  }

  get displayTabs(): { index: number; url: string; title: string; active: boolean }[] {
    return this._tabOrder
      .map(idx => this.tabs.find(t => t.index === idx))
      .filter((t): t is NonNullable<typeof t> => t != null);
  }

  private setTabs(newTabs: typeof this.tabs): void {
    this.tabs = newTabs;
    const currentIndices = new Set(newTabs.map(t => t.index));
    this._tabOrder = this._tabOrder.filter(idx => currentIndices.has(idx));
    for (const tab of newTabs) {
      if (!this._tabOrder.includes(tab.index)) {
        this._tabOrder.push(tab.index);
      }
    }
  }

  public onTabDrop(event: CdkDragDrop<any>): void {
    moveItemInArray(this._tabOrder, event.previousIndex, event.currentIndex);
  }

  private async createSession(): Promise<void> {
    const url = `${ConfigService.muxUrl}/browser/sessions`;
    const body: any = { session_id: this.sessionId };
    if (this.initialUrl) {
      body.url = this.initialUrl;
    }
    // Pass actual container dimensions so the initial viewport matches immediately
    if (this.viewportContainerEl?.nativeElement) {
      const el = this.viewportContainerEl.nativeElement;
      const w = Math.round(el.clientWidth);
      const h = Math.round(el.clientHeight);
      if (w > 0 && h > 0) {
        body.width = w;
        body.height = h;
      }
    }
    this.http.post<any>(url, body).subscribe({
      next: (info) => {
        this.currentUrl = info.url;
        this.pageTitle = info.title || 'Browser';
        this.urlBarValue = info.url;
        if (info.tabs) this.setTabs(info.tabs);
        this.updateWidgetContext();
        this.connectStream();
        this.cdr.detectChanges();
        setTimeout(() => this.viewportContainerEl?.nativeElement?.focus(), 100);
      },
      error: (err) => {
        const detail = err.error?.detail || err.message;
        this.error = `Failed to create session: ${detail}`;
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private async connectStream(): Promise<void> {
    this.disconnect();
    this._abortController = new AbortController();

    const streamUrl = `${ConfigService.muxUrl}/browser/sessions/${this.sessionId}/stream`;
    const token = await this.getAuthToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
      const response = await fetch(streamUrl, {
        headers,
        signal: this._abortController.signal
      });

      if (!response.ok) {
        this.error = `Stream failed: ${response.status}`;
        this.cdr.detectChanges();
        return;
      }

      this.connected = true;
      this.cdr.detectChanges();

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const payload = JSON.parse(line.slice(6));
              this.ngZone.run(() => {
                if (payload.type === 'frame') {
                  this.onFrame(payload);
                }
              });
            } catch {
              // keepalive or parse error
            }
          }
        }
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        this.connected = false;
        this.cdr.detectChanges();
        this.scheduleReconnect();
      }
    }
  }

  private scheduleReconnect(): void {
    if (this._destroyed || this._reconnectTimer) return;
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      if (!this._destroyed) this.connectStream();
    }, 2000);
  }

  private onFrame(payload: any): void {
    if (this._currentFrameUrl) {
      URL.revokeObjectURL(this._currentFrameUrl);
    }
    const binary = atob(payload.data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'image/jpeg' });
    this._currentFrameUrl = URL.createObjectURL(blob);

    if (this.viewportEl?.nativeElement) {
      this.viewportEl.nativeElement.src = this._currentFrameUrl;
    }

    const prevUrl = this.currentUrl;
    this.currentUrl = payload.url || this.currentUrl;
    this.pageTitle = payload.title || this.pageTitle;
    this.frameCount = payload.frame_count || this.frameCount;
    if (payload.viewport_width) this.viewportWidth = payload.viewport_width;
    if (payload.viewport_height) this.viewportHeight = payload.viewport_height;
    if (payload.tabs) this.setTabs(payload.tabs);
    this.connected = true;
    this.loading = false;
    if (this.currentUrl !== prevUrl) {
      this.urlBarValue = this.currentUrl;
      this.updateWidgetContext();
      // Auto-focus viewport so keystrokes go to the remote page, not the URL bar
      if (document.activeElement !== this.urlInput?.nativeElement) {
        this.viewportContainerEl?.nativeElement?.focus();
      }
    }

    // Detect container/viewport mismatch and trigger a re-resize.
    // The gray bar appears when the container changed size after the last resize.
    if (this.viewportContainerEl?.nativeElement) {
      const el = this.viewportContainerEl.nativeElement;
      const cw = Math.round(el.clientWidth);
      const ch = Math.round(el.clientHeight);
      if (cw > 0 && ch > 0 && (cw !== this.viewportWidth || ch !== this.viewportHeight)) {
        clearTimeout(this._resizeDebounceTimer);
        this._resizeDebounceTimer = setTimeout(() => this.resizeViewport(cw, ch), 150);
      }
    }

    this.cdr.detectChanges();
  }

  public navigate(url?: string): void {
    const target = url || this.urlBarValue;
    if (!target) return;

    let fullUrl = target;
    if (!target.match(/^(https?:\/\/|about:|data:|file:)/)) {
      fullUrl = 'https://' + target;
    }
    this.urlBarValue = fullUrl;
    this.loading = true;

    this.http.post<any>(
      `${ConfigService.muxUrl}/browser/sessions/${this.sessionId}/navigate`,
      { url: fullUrl }
    ).subscribe({
      next: (info) => {
        this.currentUrl = info.url;
        this.pageTitle = info.title || 'Browser';
        this.urlBarValue = info.url;
        this.loading = false;
        this.viewportContainerEl?.nativeElement?.focus();
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private resizeViewport(width: number, height: number): void {
    this.http.post<any>(
      `${ConfigService.muxUrl}/browser/sessions/${this.sessionId}/resize`,
      { width, height }
    ).subscribe({
      next: (info) => {
        this.viewportWidth = info.width;
        this.viewportHeight = info.height;
      }
    });
  }

  public onUrlKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.navigate();
    }
  }

  private clientToViewport(clientX: number, clientY: number): { x: number, y: number } | null {
    if (!this.viewportEl?.nativeElement) return null;
    const img = this.viewportEl.nativeElement;
    const rect = img.getBoundingClientRect();

    const relX = clientX - rect.left;
    const relY = clientY - rect.top;
    if (relX < 0 || relY < 0 || relX > rect.width || relY > rect.height) return null;

    return {
      x: Math.round((relX / rect.width) * this.viewportWidth),
      y: Math.round((relY / rect.height) * this.viewportHeight),
    };
  }

  public onViewportMousedown(event: MouseEvent): void {
    event.preventDefault();
    this.viewportContainerEl?.nativeElement?.focus();
    const pos = this.clientToViewport(event.clientX, event.clientY);
    if (!pos) return;

    this._mouseIsDown = true;
    this._dragStartPos = pos;
    this._hasDragged = false;

    const button = event.button === 2 ? 'right' : 'left';
    this.http.post<any>(
      `${ConfigService.muxUrl}/browser/sessions/${this.sessionId}/mousedown`,
      { ...pos, button }
    ).subscribe();
  }

  public onViewportMouseup(event: MouseEvent): void {
    const pos = this.clientToViewport(event.clientX, event.clientY);
    if (!pos) return;

    const button = event.button === 2 ? 'right' : 'left';
    const wasDrag = this._hasDragged;
    this._mouseIsDown = false;
    this._dragStartPos = null;
    this._hasDragged = false;

    this.http.post<any>(
      `${ConfigService.muxUrl}/browser/sessions/${this.sessionId}/mouseup`,
      { ...pos, button }
    ).subscribe({
      next: (info) => {
        if (!wasDrag) {
          this.currentUrl = info.url;
          this.pageTitle = info.title || this.pageTitle;
          this.urlBarValue = info.url;
          this.cdr.detectChanges();
        }
      }
    });
  }

  public onViewportDblclick(event: MouseEvent): void {
    event.preventDefault();
    const pos = this.clientToViewport(event.clientX, event.clientY);
    if (!pos) return;

    this.http.post<any>(
      `${ConfigService.muxUrl}/browser/sessions/${this.sessionId}/dblclick`,
      pos
    ).subscribe({
      next: (info) => {
        this.currentUrl = info.url;
        this.pageTitle = info.title || this.pageTitle;
        this.urlBarValue = info.url;
        this.cdr.detectChanges();
      }
    });
  }

  public onViewportMousemove(event: MouseEvent): void {
    const pos = this.clientToViewport(event.clientX, event.clientY);
    if (!pos) return;

    if (this._mouseIsDown && this._dragStartPos) {
      const dx = Math.abs(pos.x - this._dragStartPos.x);
      const dy = Math.abs(pos.y - this._dragStartPos.y);
      if (dx > 3 || dy > 3) this._hasDragged = true;
    }

    if (this._mousemoveThrottleTimer) return;
    this._mousemoveThrottleTimer = setTimeout(() => { this._mousemoveThrottleTimer = null; }, 50);

    this.http.post<any>(
      `${ConfigService.muxUrl}/browser/sessions/${this.sessionId}/mousemove`,
      pos
    ).subscribe();
  }

  public onViewportContextmenu(event: MouseEvent): void {
    event.preventDefault();
  }

  private _scrollThrottleTimer: any = null;

  public onViewportWheel(event: WheelEvent): void {
    event.preventDefault();
    if (this._scrollThrottleTimer) return;

    const pos = this.clientToViewport(event.clientX, event.clientY);
    if (!pos) return;

    this._scrollThrottleTimer = setTimeout(() => { this._scrollThrottleTimer = null; }, 100);

    this.http.post<any>(
      `${ConfigService.muxUrl}/browser/sessions/${this.sessionId}/scroll`,
      { ...pos, deltaX: Math.round(event.deltaX), deltaY: Math.round(event.deltaY) }
    ).subscribe();
  }

  public onViewportKeydown(event: KeyboardEvent): void {
    if (event.target === this.urlInput?.nativeElement) return;

    // Let paste events through to onViewportPaste
    if (event.key === 'v' && (event.ctrlKey || event.metaKey)) return;

    const mod = event.ctrlKey || event.metaKey;
    if (mod) {
      switch (event.key.toLowerCase()) {
        case 'l':
          event.preventDefault();
          this.urlInput?.nativeElement?.focus();
          this.urlInput?.nativeElement?.select();
          return;
        case 't':
          event.preventDefault();
          this.newTab();
          return;
        case 'w':
          event.preventDefault();
          if (this.tabs.length > 1) {
            const activeTab = this.tabs.find(t => t.active);
            if (activeTab) this.closeTab(activeTab.index, new MouseEvent('click'));
          }
          return;
        case 'r':
          event.preventDefault();
          this.refresh();
          return;
        default:
          event.preventDefault();
          const combo = `Control+${event.key.toLowerCase()}`;
          this.http.post<any>(
            `${ConfigService.muxUrl}/browser/sessions/${this.sessionId}/key`,
            { key: combo }
          ).subscribe();
          return;
      }
    }

    event.preventDefault();
    if (event.key.length === 1) {
      this.http.post<any>(
        `${ConfigService.muxUrl}/browser/sessions/${this.sessionId}/type`,
        { text: event.key }
      ).subscribe();
    } else {
      this.http.post<any>(
        `${ConfigService.muxUrl}/browser/sessions/${this.sessionId}/key`,
        { key: event.key }
      ).subscribe();
    }
  }

  public onViewportPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text');
    if (text) {
      this.http.post<any>(
        `${ConfigService.muxUrl}/browser/sessions/${this.sessionId}/paste`,
        { text }
      ).subscribe();
    }
  }

  public newTab(): void {
    this.http.post<any>(
      `${ConfigService.muxUrl}/browser/sessions/${this.sessionId}/tabs`,
      {}
    ).subscribe({
      next: (info) => {
        this.currentUrl = info.url;
        this.pageTitle = info.title || 'New Tab';
        this.urlBarValue = info.url;
        if (info.tabs) this.setTabs(info.tabs);
        this.cdr.detectChanges();
        this.urlInput?.nativeElement?.focus();
        this.urlInput?.nativeElement?.select();
      }
    });
  }

  public switchTab(index: number): void {
    this.http.post<any>(
      `${ConfigService.muxUrl}/browser/sessions/${this.sessionId}/tabs/${index}/activate`,
      {}
    ).subscribe({
      next: (info) => {
        this.currentUrl = info.url;
        this.pageTitle = info.title || 'Browser';
        this.urlBarValue = info.url;
        if (info.tabs) this.setTabs(info.tabs);
        this.cdr.detectChanges();
        this.viewportContainerEl?.nativeElement?.focus();
      }
    });
  }

  public closeTab(index: number, event: MouseEvent): void {
    event.stopPropagation();
    if (this.tabs.length <= 1) return;
    this.http.delete<any>(
      `${ConfigService.muxUrl}/browser/sessions/${this.sessionId}/tabs/${index}`
    ).subscribe({
      next: (info) => {
        this.currentUrl = info.url;
        this.pageTitle = info.title || 'Browser';
        this.urlBarValue = info.url;
        if (info.tabs) this.setTabs(info.tabs);
        this.cdr.detectChanges();
      }
    });
  }

  public copySetupInstructions(): void {
    if (this.setupRequired?.instructions) {
      navigator.clipboard.writeText(this.setupRequired.instructions);
    }
  }

  public goBack(): void {
    this.http.post<any>(
      `${ConfigService.muxUrl}/browser/sessions/${this.sessionId}/back`,
      {}
    ).subscribe({
      next: (info) => {
        this.currentUrl = info.url;
        this.pageTitle = info.title || this.pageTitle;
        this.urlBarValue = info.url;
        this.cdr.detectChanges();
      }
    });
  }

  public goForward(): void {
    this.http.post<any>(
      `${ConfigService.muxUrl}/browser/sessions/${this.sessionId}/forward`,
      {}
    ).subscribe({
      next: (info) => {
        this.currentUrl = info.url;
        this.pageTitle = info.title || this.pageTitle;
        this.urlBarValue = info.url;
        this.cdr.detectChanges();
      }
    });
  }

  public refresh(): void {
    this.navigate(this.currentUrl);
  }

  private disconnect(): void {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
    if (this._currentFrameUrl) {
      URL.revokeObjectURL(this._currentFrameUrl);
      this._currentFrameUrl = null;
    }
  }

  close(): void {
    this.disconnect();
    super.close();
  }
}
